/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, NgZone, OnChanges, OnInit, SimpleChanges, signal} from '@angular/core';
import {UIRouterModule} from '@uirouter/angular';
import * as _ from 'lodash';

import template from './default-item-title.component.html';
import {objectTypeStateParams} from '../common/filters/object-type-state-params';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {calculateCrumbs} from '../common/controller/crumb-calculator';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {CreativeSetInfoApiService} from '../creative-set/creative-set-info-api.service';
import {DataViewApiService} from '../common/controller/data-view-api.service';
import {APP_SHARED} from '../common/app-shared';

export interface DefaultItemTitleCrumb {
  id?: string;
  name: string;
  obj: any;
  objectType: any;
  perm?: boolean;
  labelOverride?: string;
}

@Component({
  standalone: true,
  imports: [...APP_SHARED, UIRouterModule],
  selector: 'adn-default-item-title',
  template
})
export class DefaultItemTitleComponent implements OnInit, OnChanges {
  @Input() model: any;
  @Input() crumbParams: any[];
  @Input() titleVariant: string;
  @Input() lineItemInfo: any;
  @Input() folioFromParam: any;
  @Input() advertiserFromParam: any;
  @Input() workspace: any;
  @Input() isNew: boolean;

  private _objectTypeKey: string;

  get objectTypeKey(): string {
    return this._objectTypeKey;
  }

  @Input()
  set objectTypeKey(value: string) {
    this._objectTypeKey = value;
  }

  /** Derived from objectTypeKey rather than stored, so it can never be read stale within a change-detection pass. */
  get objectType(): any {
    return this._objectTypeKey ? OBJECT_TYPE[this._objectTypeKey] : null;
  }

  private readonly itemNameState = signal('');
  private readonly crumbsState = signal<DefaultItemTitleCrumb[]>([]);

  @Input()
  get itemName(): string { return this.itemNameState(); }
  set itemName(value: string) { this.itemNameState.set(value); }

  @Input()
  get crumbs(): DefaultItemTitleCrumb[] { return this.crumbsState(); }
  set crumbs(value: DefaultItemTitleCrumb[]) { this.crumbsState.set(value || []); }

  private persisted = true;
  private creativeCrumbsLoaded = false;
  private crumbCtrl: {perms: any; sectionPerms: any};
  private initialized = false;

  constructor(
    @Inject(LocalUserPermissions) private readonly localUserPermissions: any,
    private readonly dataViewApi: DataViewApiService,
    private readonly creativeSetInfoApi: CreativeSetInfoApiService,
    private readonly ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.tryInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.objectTypeKey || changes.model) {
      this.tryInit();
    }
  }

  private tryInit(): void {
    if (this.initialized || !this.objectTypeKey) {
      return;
    }
    this.initialized = true;

    this.crumbCtrl = {
      perms: this.localUserPermissions.getAllVisPermissions(),
      sectionPerms: this.localUserPermissions.getSectionPerms()
    };
    this.persisted = !this.isNew;

    if (this.titleVariant === 'workspace') {
      this.initWorkspaceTitle();
      return;
    }

    this.itemName = this.resolveItemName(this.model);
    this.initVariantCrumbs();

  }

  crumbStateParams(crumb: DefaultItemTitleCrumb): Record<string, any> {
    return objectTypeStateParams(crumb.obj, crumb.objectType, crumb.obj);
  }

  private initWorkspaceTitle(): void {
    const workspace = this.workspace || this.model;
    this.itemName = workspace?.name || 'New';
  }

  private initVariantCrumbs(): void {
    if (this.titleVariant === 'creative') {
      this.loadCreativeCrumbs();
    } else if (this.titleVariant === 'folio') {
      this.applyFolioCrumbs(this.model);
    } else if (this.titleVariant === 'creative-set') {
      this.loadCreativeSetParentCrumbs(this.model);
    } else if (this.objectTypeKey === 'LineItem') {
      this.applyLineItemCrumbs(this.model);
    }
  }

  private lineItemCrumbData(data: any): any {
    if (!data) {
      return null;
    }
    return {
      ...data,
      advertiser: data.advertiser || this.advertiserFromParam
    };
  }

  private applyLineItemCrumbs(data: any): void {
    if (!this.crumbParams?.length || !this.crumbCtrl) {
      return;
    }
    const crumbData = this.lineItemCrumbData(data);
    if (!crumbData) {
      return;
    }
    const newCrumbs: DefaultItemTitleCrumb[] = [];
    const theCrumbs = _.filter(this.crumbParams, (c) => {
      return c.func ? this.localUserPermissions[c.func]() : true;
    });
    _.forEach(theCrumbs, (lCrumb) => {
      calculateCrumbs(this.crumbCtrl, lCrumb.type || lCrumb, crumbData, newCrumbs);
    });
    this.crumbs = newCrumbs;
  }

  private resolveItemName(source: any): string {
    const paramOnResponse = this.objectType?.paramOnResponse;
    if (paramOnResponse) {
      return _.get(source, [paramOnResponse, 'name']) || 'New';
    }
    return source?.name || source?.fileName || 'New';
  }

  private applyFolioCrumbs(data: any): void {
    if (!this.localUserPermissions.canBeShownAdvertiser()) {
      this.crumbs = [];
      return;
    }
    const newCrumbs: DefaultItemTitleCrumb[] = [];
    calculateCrumbs(this.crumbCtrl, 'Advertiser', data, newCrumbs);
    this.crumbs = newCrumbs;
  }

  private applyCreativeSetCrumbs(crumbSource: any): void {
    const newCrumbs: DefaultItemTitleCrumb[] = [];
    _.forEach(['Advertiser', 'Folio'], (lCrumb) => {
      calculateCrumbs(this.crumbCtrl, lCrumb, crumbSource, newCrumbs);
    });
    this.crumbs = newCrumbs;
  }

  private loadCreativeSetParentCrumbs(data: any): void {
    const folio = _.get(data, ['folio']) || this.folioFromParam;
    const advertiser = _.get(data, ['advertiser']) || this.advertiserFromParam;
    if (_.get(folio, 'id') || _.get(advertiser, 'id')) {
      this.applyCreativeSetCrumbs({folio: folio, advertiser: advertiser});
    }
    if (!this.persisted || !this.model?.id) {
      return;
    }
    if (_.get(folio, 'id') && _.get(advertiser, 'id')) {
      return;
    }
    this.creativeSetInfoApi.get(this.model.id).subscribe((cs) => {
      this.ngZone.run(() => {
        this.applyCreativeSetCrumbs({
          folio: _.get(cs, 'folio') || folio,
          advertiser: _.get(cs, 'advertiser') || advertiser
        });
      });
    });
  }

  private loadCreativeCrumbs(): void {
    if (this.creativeCrumbsLoaded && this.crumbs.length > 0) {
      return;
    }

    const lineItemInfo = this.lineItemInfo || {};
    const parentObject = lineItemInfo.id ? lineItemInfo : this.model?.lineItem ? this.model.lineItem : this.model?.creativeSet;
    if (!parentObject?.id) {
      this.crumbs = [];
      return;
    }

    if (lineItemInfo.fromLineItem || _.get(this.model, ['lineItem', 'id'])) {
      this.dataViewApi.getLineItemViewFromCache(parentObject.id).subscribe((dataViewObj) => {
        this.ngZone.run(() => {
          const newCrumbs: DefaultItemTitleCrumb[] = [];
          let crumbs = ['Advertiser', 'Order', 'LineItem'];
          if (this.localUserPermissions.isSelfService() || this.localUserPermissions.isAxPublisherOnly()) {
            crumbs = ['LineItem'];
          }
          _.forEach(crumbs, (lCrumb) => {
            calculateCrumbs(this.crumbCtrl, lCrumb, dataViewObj, newCrumbs);
          });
          this.crumbs = newCrumbs;
          this.creativeCrumbsLoaded = true;
        });
      });
      return;
    }

    if (lineItemInfo.fromCreativeSet || _.get(this.model, ['creativeSet', 'id'])) {
      const doCreativeSetCrumbs = (dataViewObj: any) => {
        const newCrumbs: DefaultItemTitleCrumb[] = [];
        _.forEach(['Advertiser', 'Folio', 'CreativeSet'], (lCrumb) => {
          calculateCrumbs(this.crumbCtrl, lCrumb, dataViewObj, newCrumbs);
        });
        this.crumbs = newCrumbs;
        this.creativeCrumbsLoaded = true;
      };

      if (_.get(this.model, ['creativeSet', 'id']) && !_.get(lineItemInfo, ['advertiser', 'id'])) {
        this.creativeSetInfoApi.get(this.model.creativeSet.id).subscribe((cs) => {
          this.ngZone.run(() => {
            doCreativeSetCrumbs({
              creativeSet: this.model.creativeSet,
              folio: cs.folio,
              advertiser: cs.advertiser
            });
          });
        });
      } else {
        doCreativeSetCrumbs({
          creativeSet: {
            id: lineItemInfo.id,
            name: lineItemInfo.name
          },
          advertiser: {
            id: lineItemInfo.advertiserId,
            name: lineItemInfo.advertiserName
          }
        });
      }
      return;
    }

    this.crumbs = [];
  }
}
